import { AnalysisCostType } from '@marmon/shared/types/analysis';
import { ReportType, ReportTypes } from '../constants/report-type.const';
import { ReportSlide, ReportSlideKey, ReportSlideKeys } from '../interfaces';

/**
 * Hard cap for a slide header or subheader. Both are a single line.
 */
export const SlideHeadingMaxLength = 50;

export type ReportSlideHeadingContext = {
	reportType?: ReportType;
	costType?: AnalysisCostType;
};

export type SlideHeadingDefault = {
	header: string;
	subheader: string;
	showSubheader: boolean;
};

export type SlideHeadingSource = {
	key: ReportSlideKey;
	header?: string;
	subheader?: string;
	showSubheader?: boolean;
};

export type SlideHeadingBackfillPlan =
	| { action: 'skip'; reason: 'unknown-key' | 'current' }
	| {
			action: 'update';
			header: string;
			subheader: string;
			showSubheader: boolean;
	  };

const ReportSlideKeySet: ReadonlySet<string> = new Set(ReportSlideKeys);

const heading = (header: string, subheader = ''): SlideHeadingDefault => ({
	header,
	subheader,
	showSubheader: subheader.length > 0,
});

/**
 * Built-in title copy, keyed by slide. `report-title` and `quad-margin`
 * depend on the report or analysis, so they are resolved separately.
 */
const StaticSlideHeadings = {
	'report-contents': heading('Contents'),
	'profitability-section': heading('80/20 Profitability Analysis'),
	'quad-section': heading('Quad Analysis'),
	'quad-period-comparison-overview': heading('Quads', 'Period Comparisons'),
	'customer-product-section': heading('Customer & Product Distributions'),
	'quartile-section': heading('Quartile Analysis'),
	'quad-4-section': heading('Quad 4 Analysis'),
	'quad-1-section': heading('Quad 1 Analysis'),
	'recommendations-section': heading('Recommendations'),
	'appendix-section': heading('Appendix'),
	'appendix-quad-table': heading('Appendix A', 'Quad Table'),
	'appendix-quad-summary': heading('Appendix B', 'Quad Summary'),
	'appendix-quartile-table': heading('Appendix C', 'Quartile Table'),
	'appendix-quartile-summary': heading('Appendix D', 'Quartile Summary'),
	'report-executive-summary': heading('Executive Summary'),
	'period-comparison-overview': heading('P/L Overview', 'Period Comparison'),
	'profitability-curve-actual': heading(
		'Profitability Curve',
		'Before 80/20',
	),
	'profitability-curve-phase-1': heading(
		'Profitability Curve',
		'Phase 1: Lifting the Curve',
	),
	'profitability-curve-phase-2': heading(
		'Profitability Curve',
		'Phase 2: Focus on the 80s',
	),
	'profitability-curve-model': heading(
		'Profitability Curve',
		'Before & After',
	),
	'profitability-curve-overhead': heading(
		'Profitability Curve',
		'Overhead Comparison',
	),
	'quad-overview': heading('Quads', 'Overview'),
	'quad-revenue-distribution': heading(
		'Quads',
		'Revenue Distribution Assessment',
	),
	'quad-period-comparison-1-2': heading(
		'Quads',
		'Period Comparison: Quads 1 & 2',
	),
	'quad-period-comparison-3-4': heading(
		'Quads',
		'Period Comparison: Quads 3 & 4',
	),
	'customer-distribution': heading('80/20 Distribution', 'Customers'),
	'product-distribution': heading('80/20 Distribution', 'Products'),
	'quartile-customer-product-revenue': heading(
		'Quartiles',
		'Customer & Product Revenue',
	),
	'quartile-customer-breakdown': heading(
		'Quartiles',
		'Customer Opportunities',
	),
	'quad-4-customer-observations': heading('Quad 4 Observations', 'Customers'),
	'quad-4-product-observations': heading('Quad 4 Observations', 'Products'),
	'quad-4-breakeven': heading('Breakeven Volume', 'Quad 4'),
	'quad-1-top-customers': heading('Quad 1 Analysis', 'Top 80s Customers'),
	'quad-1-top-products': heading('Quad 1 Analysis', 'Top 80s Products'),
	'recommendations-overview': heading('Recommendations'),
	'qbr-section': heading('Quad and Quartile Overview'),
} as const satisfies Record<
	Exclude<ReportSlideKey, 'report-title' | 'quad-margin'>,
	SlideHeadingDefault
>;

export function isReportSlideKey(value: unknown): value is ReportSlideKey {
	return typeof value === 'string' && ReportSlideKeySet.has(value);
}

export function normalizeSlideHeading(value: string): string {
	return value
		.replace(/[\r\n]/g, '')
		.trim()
		.slice(0, SlideHeadingMaxLength);
}

/**
 * Direct cost is the fallback when an analysis cost type cannot be read.
 * That heading is contribution margin.
 */
function marginHeading(costType?: AnalysisCostType): SlideHeadingDefault {
	if (costType === 'material') {
		return heading('Quads', 'Material Margin');
	}
	return heading('Quads', 'Contribution Margin');
}

function titleHeading(reportType?: ReportType): SlideHeadingDefault {
	if (reportType === ReportTypes.QBR) return heading('QBR Report');
	return heading('80/20 Report');
}

export function getSlideHeadingDefaults(
	key: ReportSlideKey,
	context?: ReportSlideHeadingContext,
): SlideHeadingDefault {
	if (key === 'report-title') return titleHeading(context?.reportType);
	if (key === 'quad-margin') return marginHeading(context?.costType);
	return StaticSlideHeadings[key];
}

/**
 * An omitted `showSubheader` keeps the key's built-in configuration so
 * reports still show subtitles before the heading backfill writes the flag.
 * An explicit `false` stays false.
 */
export function resolveShowSubheader(
	slide: SlideHeadingSource | undefined,
	key: ReportSlideKey,
	context?: ReportSlideHeadingContext,
): boolean {
	if (typeof slide?.showSubheader === 'boolean') return slide.showSubheader;
	return getSlideHeadingDefaults(key, context).showSubheader;
}

export function resolveSlideHeader(
	slide: SlideHeadingSource | undefined,
	key: ReportSlideKey,
	context?: ReportSlideHeadingContext,
): string {
	const stored = slide?.header?.trim();
	if (stored) return stored;
	return getSlideHeadingDefaults(key, context).header;
}

export function resolveSlideSubheader(
	slide: SlideHeadingSource | undefined,
	key: ReportSlideKey,
	context?: ReportSlideHeadingContext,
): string {
	const stored = slide?.subheader?.trim() ?? '';
	if (slide?.showSubheader === false) return stored;
	if (stored) return stored;
	const defaults = getSlideHeadingDefaults(key, context);
	return defaults.showSubheader ? defaults.subheader : '';
}

/** Contents entries follow the slide's header, including later renames. */
export function slideContentsLabel(
	slide: SlideHeadingSource,
	context?: ReportSlideHeadingContext,
): string {
	return resolveSlideHeader(slide, slide.key, context);
}

export function planSlideHeadingBackfill(
	slide: {
		key?: unknown;
		header?: unknown;
		subheader?: unknown;
		showSubheader?: unknown;
	},
	context?: ReportSlideHeadingContext,
): SlideHeadingBackfillPlan {
	if (!isReportSlideKey(slide.key)) {
		return { action: 'skip', reason: 'unknown-key' };
	}

	const defaults = getSlideHeadingDefaults(slide.key, context);
	const header = typeof slide.header === 'string' ? slide.header.trim() : '';
	const subheader =
		typeof slide.subheader === 'string' ? slide.subheader.trim() : '';
	const showSubheader =
		typeof slide.showSubheader === 'boolean'
			? slide.showSubheader
			: defaults.showSubheader;
	const nextHeader = header || defaults.header;
	const nextSubheader = showSubheader
		? subheader || defaults.subheader
		: subheader;

	if (
		header === nextHeader &&
		subheader === nextSubheader &&
		slide.showSubheader === showSubheader
	) {
		return { action: 'skip', reason: 'current' };
	}

	return {
		action: 'update',
		header: nextHeader,
		subheader: nextSubheader,
		showSubheader,
	};
}

export function slideHeadingFromSlide(
	slide: ReportSlide,
	context?: ReportSlideHeadingContext,
): SlideHeadingDefault {
	const defaults = isReportSlideKey(slide.key)
		? getSlideHeadingDefaults(slide.key, context)
		: heading('');

	return {
		header: slide.header?.trim() || defaults.header,
		subheader: slide.subheader?.trim() || defaults.subheader,
		showSubheader:
			typeof slide.showSubheader === 'boolean'
				? slide.showSubheader
				: defaults.showSubheader,
	};
}
